import * as React from "react"

import { cn } from "@/utils/utils"
import { FIELD, FIELD_FOCUS, FIELD_SIZES, fieldLook, type FieldLook, type FieldSize } from "@/components/ui/recipes"

type InputProps = Omit<React.ComponentProps<"input">, "size"> & {
  size?: FieldSize
  /** Filled or outlined; left out, its size's (FIELD_LOOKS). */
  look?: FieldLook
  /** The value's unit inside the field's end (s, %, m). */
  suffix?: string
  /** Sized to its value: a pill holding the value in the semibold with its unit right after it (the Forms editor's numbers, 3D Text's speed). */
  fit?: boolean
}

/** Lureo's text field: md in forms and dialogs, sm in editor panels, xs inline in a row. */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = "md", look, suffix, fit = false, ...props }, ref) => {
    if (fit) {
      return (
        <label className={cn("inline-flex h-7 cursor-text items-center gap-0.5 rounded-lureo-radius-pill border-lureo-border-width border-lureo-line bg-lureo-surface px-2.5 font-lureo-font-ui text-lureo-14 font-semibold tabular-nums text-lureo-ink transition-colors focus-within:border-lureo-brand-ink has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-lureo-disabled-opacity", className)}>
          <input type={type} ref={ref} className="min-w-0 bg-transparent text-left outline-hidden [field-sizing:content]" {...props} />
          {suffix && <span aria-hidden className="pointer-events-none select-none font-light text-lureo-ink-subtle">{suffix}</span>}
        </label>
      )
    }
    const field = (
      <input
        type={type}
        className={cn(
          FIELD,
          fieldLook(size, look),
          FIELD_SIZES[size],
          FIELD_FOCUS[size],
          "flex file:border-0 file:bg-transparent file:text-lureo-14 file:font-medium file:text-lureo-ink file:leading-5",
          suffix && "pr-6",
          !suffix && className
        )}
        ref={ref}
        {...props}
      />
    )
    if (!suffix) return field
    // With a unit, className sizes the box the field and its unit share
    return (
      <div className={cn("relative flex items-center", className)}>
        {field}
        <span aria-hidden className="pointer-events-none absolute right-2 font-lureo-font-ui text-lureo-12 text-lureo-ink-subtle">{suffix}</span>
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
